import { Modal, ModalProps } from 'antd';
import React, { useState } from 'react';
import AppPageHeader from '../AppPageHeader';
import InfiniteTable from '../InfiniteTable';
import { ColumnsType } from 'antd/es/table/interface';
import { useTasks } from '@/core/domain/operational/Tasks/Tasks.hooks';
import { Task, TasksQuery } from '@/core/domain/operational/Tasks/Tasks.types';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { useIntl } from 'react-intl';

interface Props extends ModalProps {
  onCancel: () => void;
  open: boolean;
  onCallback: (items: Task[]) => void;
  itemSelectedIds: number[];
  multiple?: boolean;
  closeOnSelect?: boolean;
}

const SelectTasksListModal: React.FC<Props> = ({
  onCancel,
  open,
  onCallback,
  itemSelectedIds,
  multiple = true,
  closeOnSelect = true,
  ...props
}) => {
  const intl = useIntl();
  const [selectItems, setSelectItems] = useState<Task[]>([]);

  const [queryParams, setQueryParams] = useState<TasksQuery>({
    active: true,
  });
  const { data, isFetching, hasNextPage, fetchNextPage } = useTasks(
    {
      ...queryParams,
    },
    open,
  );
  const tasks = flatInfiniteData(data);

  const columns: ColumnsType<Task> = [
    {
      key: 'temparioCode',
      dataIndex: 'temparioCode',
      title: intl.formatMessage({ id: 'tasks.general.form.temporaryCode' }),
      ellipsis: true,
      width: 150,
    },
    {
      key: 'description',
      dataIndex: 'description',
      title: intl.formatMessage({ id: 'tasks.general.form.description' }),
      ellipsis: true,
      width: 350,
    },
    {
      key: 'subArea',
      dataIndex: ['subArea', 'name'],
      title: intl.formatMessage({ id: 'tasks.general.form.area' }),
      ellipsis: true,
      width: 200,
    },
  ];

  const handleCancel = () => {
    setQueryParams({ active: true });
    setSelectItems([]);

    onCancel();
  };

  const handleOk = () => {
    if (selectItems?.length) {
      onCallback(selectItems);

      if (closeOnSelect) {
        handleCancel();
      }

      setSelectItems([]);
    }
  };

  const filteredTasks = tasks.filter(
    (task) => !itemSelectedIds.includes(task.id),
  );

  return (
    <Modal
      {...props}
      width={800}
      open={open}
      title="Seleção de serviços"
      onOk={handleOk}
      okText="Incluir"
      okButtonProps={{ disabled: !selectItems?.length }}
      onCancel={handleCancel}
    >
      <AppPageHeader
        onSearch={(value) =>
          setQueryParams((prev) => ({ ...prev, search: value }))
        }
        searchValue={queryParams.search}
      />

      <InfiniteTable
        columns={columns}
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        dataSource={filteredTasks}
        loading={isFetching}
        rowKey={(row) => row.id}
        rowSelection={{
          preserveSelectedRowKeys: true,
          selectedRowKeys: [...(selectItems.map((item) => item?.id) || [])],
          getCheckboxProps: (item) => ({
            disabled:
              multiple === false &&
              !!selectItems?.length &&
              selectItems.at(-1)?.id !== item.id,
          }),
          onChange: (_, rows) => setSelectItems(rows),
          hideSelectAll: true,
        }}
      />
    </Modal>
  );
};

export default SelectTasksListModal;
